import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, Modal, FlatList, StyleSheet, ActivityIndicator } from 'react-native';
import APIService, { Marca, Modelo } from '../services/api';

interface MarcaModeloSelectorProps {
  selectedMarca?: Marca;
  selectedModelo?: Modelo;
  onMarcaChange: (marca: Marca) => void;
  onModeloChange: (modelo: Modelo) => void;
}

const MarcaModeloSelector: React.FC<MarcaModeloSelectorProps> = ({
  selectedMarca,
  selectedModelo,
  onMarcaChange,
  onModeloChange,
}): React.ReactElement => {
  const [marcas, setMarcas] = useState<Marca[]>([]);
  const [modelos, setModelos] = useState<Modelo[]>([]);
  const [loadingMarcas, setLoadingMarcas] = useState(false);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [showMarcaPicker, setShowMarcaPicker] = useState(false);
  const [showModeloPicker, setShowModeloPicker] = useState(false);

  // Cargar marcas al montar el componente
  useEffect(() => {
    loadMarcas();
  }, []);

  // Cargar modelos cuando cambia la marca
  useEffect(() => {
    if (selectedMarca) {
      loadModelos(selectedMarca.id);
    } else {
      setModelos([]);
    }
  }, [selectedMarca?.id]);

  const loadMarcas = async () => {
    setLoadingMarcas(true);
    try {
      const data = await APIService.getMarcas();
      console.log('🏍️ MarcaModeloSelector: Marcas cargadas:', data.length);
      setMarcas(data);
    } catch (error) {
      console.error('❌ Error cargando marcas:', error);
    } finally {
      setLoadingMarcas(false);
    }
  };

  const loadModelos = async (marcaId: number) => {
    setLoadingModelos(true);
    try {
      const data = await APIService.getModelos(marcaId);
      console.log('🏍️ MarcaModeloSelector: Modelos cargados:', data.length);
      setModelos(data);
    } catch (error) {
      console.error('❌ Error cargando modelos:', error);
    } finally {
      setLoadingModelos(false);
    }
  };

  return (
    <View>
      {/* Selector de Marca */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Marca de tu moto</Text>
        <TouchableOpacity 
          style={styles.selector}
          onPress={() => setShowMarcaPicker(true)}
        >
          <Text style={selectedMarca ? styles.selectedText : styles.placeholderText}>
            {selectedMarca ? selectedMarca.nombre : 'Selecciona una marca'}
          </Text>
          <Text style={styles.arrow}>▼</Text>
        </TouchableOpacity>
      </View>

      {/* Selector de Modelo */}
      {selectedMarca && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Modelo de tu moto</Text>
          <TouchableOpacity 
            style={[styles.selector, !selectedMarca && styles.selectorDisabled]}
            onPress={() => selectedMarca && setShowModeloPicker(true)}
            disabled={!selectedMarca}
          >
            <Text style={selectedModelo ? styles.selectedText : styles.placeholderText}>
              {selectedModelo ? selectedModelo.nombre : 'Selecciona un modelo'}
            </Text>
            <Text style={styles.arrow}>▼</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Modal de Marcas */}
      <Modal
        visible={showMarcaPicker}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowMarcaPicker(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Selecciona una Marca</Text>
              <TouchableOpacity onPress={() => setShowMarcaPicker(false)}>
                <Text style={styles.closeButton}>✕</Text>
              </TouchableOpacity>
            </View>

            {loadingMarcas ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#E74C3C" />
                <Text style={styles.loadingText}>Cargando marcas...</Text>
              </View>
            ) : (
              <FlatList
                data={marcas}
                keyExtractor={(item: Marca) => item.id.toString()}
                renderItem={({ item }: { item: Marca }) => (
                  <TouchableOpacity
                    style={[
                      styles.listItem,
                      selectedMarca?.id === item.id && styles.listItemSelected
                    ]}
                    onPress={() => {
                      console.log('🏍️ Marca seleccionada:', item);
                      onMarcaChange(item);
                      setShowMarcaPicker(false);
                    }}
                  >
                    <Text style={[
                      styles.listItemText,
                      selectedMarca?.id === item.id && styles.listItemTextSelected
                    ]}>
                      {item.nombre}
                    </Text>
                    {selectedMarca?.id === item.id && (
                      <Text style={styles.checkmark}>✓</Text>
                    )}
                  </TouchableOpacity>
                )}
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Modal de Modelos */}
      <Modal
        visible={showModeloPicker}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowModeloPicker(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Selecciona un Modelo</Text>
              <TouchableOpacity onPress={() => setShowModeloPicker(false)}>
                <Text style={styles.closeButton}>✕</Text>
              </TouchableOpacity>
            </View>

            {loadingModelos ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#E74C3C" />
                <Text style={styles.loadingText}>Cargando modelos...</Text>
              </View>
            ) : modelos.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>No hay modelos disponibles para esta marca</Text>
              </View>
            ) : (
              <FlatList
                data={modelos}
                keyExtractor={(item: Modelo) => item.id.toString()}
                renderItem={({ item }: { item: Modelo }) => (
                  <TouchableOpacity
                    style={[
                      styles.listItem,
                      selectedModelo?.id === item.id && styles.listItemSelected
                    ]}
                    onPress={() => {
                      console.log('🏍️ Modelo seleccionado:', item);
                      onModeloChange(item);
                      setShowModeloPicker(false);
                    }}
                  >
                    <Text style={[
                      styles.listItemText,
                      selectedModelo?.id === item.id && styles.listItemTextSelected
                    ]}>
                      {item.nombre}
                    </Text>
                    {selectedModelo?.id === item.id && (
                      <Text style={styles.checkmark}>✓</Text>
                    )}
                  </TouchableOpacity>
                )}
              />
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  inputGroup: {
    marginBottom: 15,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  selector: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 8,
    padding: 12,
  },
  selectorDisabled: {
    opacity: 0.5,
  },
  selectedText: {
    fontSize: 16,
    color: '#333',
  },
  placeholderText: {
    fontSize: 16,
    color: '#999',
  },
  arrow: {
    fontSize: 12,
    color: '#666',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#EEE',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    fontSize: 24,
    color: '#666',
    fontWeight: 'bold',
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: '#666',
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    color: '#999',
    textAlign: 'center',
  },
  listItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  listItemSelected: {
    backgroundColor: '#FFF5F5',
  },
  listItemText: {
    fontSize: 16,
    color: '#333',
  },
  listItemTextSelected: {
    color: '#E74C3C',
    fontWeight: '600',
  },
  checkmark: {
    fontSize: 20,
    color: '#E74C3C',
    fontWeight: 'bold',
  },
});

export default MarcaModeloSelector;
